import React, { useCallback, useMemo } from "react";

import Menu from "antd/es/menu";
import {
  addMonths,
  addQuarters,
  getMonth,
  getYear,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from "date-fns/esm";
import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

import { Deal } from "@mapmycustomers/shared/types/entity";

import BoardColumn from "@app/component/DnDBoard/types/BoardColumn";
import ForecastColumnType from "@app/scene/deal/component/ForecastPage/enum/ForecastColumnType";
import ForecastViewMode from "@app/scene/deal/component/ForecastPage/enum/ForecastViewMode";
import formatDateAsColumnKey from "@app/scene/deal/component/ForecastPage/util/formatDateAsColumnKey";
import { DatesWithDeals } from "@app/scene/deal/store/types/ForecastView";
import DateColumn from "@app/scene/deal/type/DateColumn";
import { dealForecastViewAnalytics } from "@app/scene/deal/util/dealTableAnalytics";
import { formatDate } from "@app/util/formatters";

import useNameDateFormat from "./useNameDateFormat";

const useBoardColumns = (
  forecastViewMode: ForecastViewMode,
  columnCount: number,
  groupedDeals: DatesWithDeals,
  setSelectedDateForDownload: (data: { date: Date | undefined; name: string } | null) => void,
  options?: { canDownload?: boolean }
): BoardColumn<DateColumn, Deal>[] => {
  const intl = useIntl();

  invariant(
    [ForecastViewMode.MONTH, ForecastViewMode.QUARTER].includes(forecastViewMode),
    `Invalid forecastViewMode: ${forecastViewMode}`
  );
  const nameDateFormat = useNameDateFormat(forecastViewMode, intl);

  const handleExportClick = useCallback(
    (columnName: string, date?: Date) => {
      setSelectedDateForDownload({ date, name: columnName });
      dealForecastViewAnalytics?.clicked(["Export Column Data"], { column: columnName });
    },
    [setSelectedDateForDownload]
  );

  const exportMenuItem = useCallback(
    (analyticColumnName: string, date?: Date) => (
      <Menu.Item key="export" onClick={() => handleExportClick(analyticColumnName, date)}>
        {intl.formatMessage({
          id: "deals.forecastView.column.menu.exportData",
          defaultMessage: "Download",
          description: "Deal DND board column menu - Export Data",
        })}
      </Menu.Item>
    ),
    [handleExportClick, intl]
  );

  const olderDate = useMemo(() => {
    return forecastViewMode === ForecastViewMode.MONTH
      ? subMonths(startOfMonth(Date.now()), 1)
      : subQuarters(startOfQuarter(Date.now()), 1);
  }, [forecastViewMode]);

  const olderFormattedDate = formatDateAsColumnKey(forecastViewMode, olderDate);

  return useMemo(() => {
    const baseDate = (forecastViewMode === ForecastViewMode.MONTH ? startOfMonth : startOfQuarter)(
      Date.now()
    );

    return Array.from({ length: columnCount + 1 }, (_, index) => {
      if (index === 0) {
        const columnDeals: Deal[] = groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.deals ?? [];
        return {
          id: 0,
          data: {
            adjustedAmount: groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.adjustedAmount,
            amount: groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.amount,
            date: olderDate,
          },
          hideAddButton: true,
          infoTooltip: intl.formatMessage({
            id: "deals.forecastView.olderColumn.info",
            defaultMessage:
              "View deals in which the closing date falls within previous months or quarters. Note that past deals within an active month or quarter will still display in their respective column.",
            description:
              "Older deals column desciption for having deals from previous months and quarters",
          }),
          items: columnDeals,
          loading: !!groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.loading,
          menuItems:
            options?.canDownload === false ? [] : [exportMenuItem(olderFormattedDate, olderDate)],
          name: intl.formatMessage({
            id: "deals.forecastView.column.name.older",
            defaultMessage: "Older",
            description: "Column for deals with older closing date in Deals Forecast view",
          }),
          total: groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.total,
          type: groupedDeals[ForecastColumnType.PAST_CLOSING_DATE]?.type,
        };
      }

      if (index === 1) {
        const columnDeals: Deal[] = groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.deals ?? [];
        return {
          id: 1,
          data: {
            adjustedAmount: groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.adjustedAmount,
            amount: groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.amount,
            date: undefined,
          },
          hideAddButton: true,
          items: columnDeals,
          loading: !!groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.loading,
          menuItems:
            options?.canDownload === false ? [] : [exportMenuItem("No closing date", undefined)],
          name: intl.formatMessage({
            id: "deals.forecastView.column.name.noClosingDate",
            defaultMessage: "No closing date",
            description: "Column for deals with no closing date in Deals Forecast view",
          }),
          total: groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.total,
          type: groupedDeals[ForecastColumnType.NO_CLOSING_DATE]?.type,
        };
      }

      const date = (forecastViewMode === ForecastViewMode.MONTH ? addMonths : addQuarters)(
        baseDate,
        index - 2
      );
      const formattedDate = formatDateAsColumnKey(forecastViewMode, date);
      const columnDeals: Deal[] = groupedDeals[formattedDate]?.deals ?? [];

      return {
        id:
          (forecastViewMode === ForecastViewMode.MONTH ? 10000 : 20000) +
          (getYear(date) % 100) * 100 +
          getMonth(date),
        data: {
          adjustedAmount: groupedDeals[formattedDate]?.adjustedAmount,
          amount: groupedDeals[formattedDate]?.amount,
          date,
        },
        items: columnDeals,
        loading: !!groupedDeals[formattedDate]?.loading,
        menuItems: options?.canDownload === false ? [] : [exportMenuItem(formattedDate, date)],
        name: formatDate(date, nameDateFormat),
        total: groupedDeals[formattedDate]?.total,
        type: groupedDeals[formattedDate]?.type,
      };
    });
  }, [
    columnCount,
    exportMenuItem,
    forecastViewMode,
    groupedDeals,
    intl,
    olderDate,
    olderFormattedDate,
    nameDateFormat,
    options?.canDownload,
  ]);
};

export default useBoardColumns;
